import { useState } from 'react';
import flatMap from 'lodash/flatMap';
import get from 'lodash/get';
import flatten from 'lodash/flatten';

import { getIdFromGid } from '../utils';
import { useGlobalContext } from '../state/GlobalContext';
import {
  listFormatter,
  getCurrencyFormatter,
  getGidFromIdForResource,
} from '../../utils';
import {
  emptyGlobalProduct,
  emptyGlobalVariant,
} from '../../utils/globalObjects';
import { multiSelectType } from '../../components/MultiSelectDropdown';
import AddonsModalContent from './AddonsModalContent';

import { getAddons, changeSCL, LineRequest, LineResponse } from '../api';
import useGlobalModalContext from '../../state/GlobalModalContext';
import { useQuery, useQueryClient } from 'react-query';

const CURRENCY = get(window, ['Shopify', 'currency', 'active'], 'USD');
const VARIANT_DEFAULT_TITLE = 'Default Title';

export enum BundleType {
  dynamicBaB = 'dynamicBaB',
  classicBaB = 'buildABox',
  shuffleBab = 'shuffle',
}

export interface AddonProduct {
  productData: GlobalProduct;
  addonGroupId?: string;
  featuredOrder: number;
  groupOrder: number;
  hasOnlyDefaultVariant: boolean;
  id: number;
}

export interface slidePanelAddonProps {
  name: string;
  variantsOptions: MultiSelectOption[];
  optionsAndValues: MultiSelectOption[];
  hasOnlyDefaultVariant: boolean;
}

interface UseAddonsResponse {
  addons: GlobalVariant[];
  addonProducts: AddonProduct[];
  isLoading: boolean;
  error: string | null;
  saveBundleAdded: (
    bundleProps,
    _lineId: string,
    selectedItems: any,
    sellingPlan?: string | number,
    discountCodes?: string[],
    oneTimeAddon?: boolean,
    addonGroupId?: string,
  ) => void;
  saveAddonsAdded: (addonLines: LineRequest[]) => void;
}

export const useAddons = (
  onAddAddons: (
    addons: (GlobalVariant & { quantity: number; productName: string } & {
      line: { id: string; isOneTime?: boolean };
    })[],
  ) => void,
  onAddBundle: (
    bundleProps,
    _lineId: string,
    selectedItems: any,
    sellingPlan?: string | number,
    discountCodes?: string[],
    oneTimeAddon?: boolean,
    addonGroupId?: string,
  ) => void,
  addonsDiscounts?: AddonsDiscounts | null,
): UseAddonsResponse => {
  const { customerId, subscriptionId, authToken } = useGlobalContext();

  const [addonProducts, setAddonProducts] = useState<AddonProduct[]>([]);

  const [error, setError] = useState<string | null>(null);

  const { data: addons = [], isLoading } = useQuery(
    ['addons'],
    async () => {
      if (subscriptionId && customerId && authToken) {
        const fetchAddons = await getAddons(subscriptionId, {
          customerId,
          authToken,
        });
        const formattedAddons = transformResponse(fetchAddons, addonsDiscounts);
        setAddonProducts(formattedAddons);

        const addonsCollections: GlobalVariant[] = flatMap(
          formattedAddons,
          (product) => extractVariantAndImage(product, addonsDiscounts),
        ) as unknown as GlobalVariant[];
        return addonsCollections;
      }
    },
    { refetchOnWindowFocus: false },
  );

  const saveAddonsAdded = async (addonLines: LineRequest[]) => {
    try {
      const sclUpdated = await changeSCL(
        addonLines,
        customerId,
        subscriptionId,
        authToken,
      );
      if (sclUpdated.subscriptionContractId) {
        const addonsAdded = getVariantDataFromAddonLines(
          addonLines,
          addons,
          sclUpdated.lines,
          sclUpdated.updatedPrices,
          addonProducts,
        );
        onAddAddons(addonsAdded);
      }
    } catch (e: any) {
      setError(e.message);
    }
  };

  return {
    isLoading,
    addons,
    error,
    addonProducts,
    saveBundleAdded: onAddBundle,
    saveAddonsAdded,
  };
};

export interface UseAddonsSelectorResponse {
  handleSelect: (selectedAddon: { id: string; quantity: number }) => void;
  handleConfirm: (selectedOption: unknown) => void;
  selectAddon: (addonId: number, isBundle: boolean) => void;
  handleSaveBundle: (
    bundleProps: any,
    _lineId: string,
    selectedItems: any,
    sellingPlan?: string | number,
    discountCodes?: string[],
    oneTimeAddon?: boolean,
    addonGroupId?: string,
  ) => void;
  slidePanelAddon?: GlobalProduct & { addonGroupId?: string };
  selectedVariant?: GlobalVariant;
  selectedBundle: GlobalProduct & { addonGroupId?: string };
  isPanelOpen: boolean;
  closePanel: () => void;
}

export const useAddonsSelector = (
  addons: GlobalVariant[],
  addonProducts: AddonProduct[],
  saveAddonsAdded: (addons: LineRequest[]) => void,
  saveBundleAdded: (
    newBundleProps,
    _lineId: string,
    dynamicBabItems?: unknown,
    sellingPlan?: string | number,
    discountCodes?: string[],
    oneTimeAddon?: boolean,
    addonGroupId?: string,
  ) => void,
): UseAddonsSelectorResponse => {
  const [isPanelOpen, setPanelOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<GlobalVariant>();
  const [slidePanelAddon, setSlidePanelAddon] = useState<
    GlobalProduct & { addonGroupId?: string }
  >();
  const [selectedBundle, setSelectedBundle] = useState<
    GlobalProduct & { addonGroupId?: string }
  >();
  const queryClient = useQueryClient();
  const { customerId, subscriptionId } = useGlobalContext();
  const selectAddon = (id: number, isBundle: boolean) => {
    const selectedAddonProduct = addonProducts.find(
      (a) => a.productData.id === id,
    );

    if (selectedAddonProduct) {
      const format = getCurrencyFormatter(
        selectedAddonProduct.productData.priceCurrency ?? CURRENCY,
      );
      const formattedPrice = format(
        +selectedAddonProduct.productData.variants[0].price as number,
      );
      const formattedDiscountedPrice = selectedAddonProduct.productData
        .variants[0].discountedPrice
        ? format(
            selectedAddonProduct.productData.variants[0]
              .discountedPrice as number,
          )
        : undefined;

      if (isBundle) {
        setSelectedBundle({
          ...selectedAddonProduct.productData,
          addonGroupId: selectedAddonProduct.addonGroupId,
        });
        setSelectedVariant(null);
        setSlidePanelAddon(undefined);
        setPanelOpen(false);
      } else {
        setSelectedVariant({
          ...selectedAddonProduct.productData.variants[0],
          price: formattedPrice,
          discountedPrice: formattedDiscountedPrice,
        });
        setSlidePanelAddon({
          ...selectedAddonProduct.productData,
          addonGroupId: selectedAddonProduct.addonGroupId,
        });
        setPanelOpen(true);
      }
    }
  };

  const handleSaveBundle = async (
    newBundleProps,
    _lineId: string,
    dynamicBabItems?: unknown,
    sellingPlan?: string | number,
    discountCodes?: string[],
    oneTimeAddon?: boolean,
    addonGroupId?: string,
  ) => {
    await saveBundleAdded(
      newBundleProps,
      _lineId,
      dynamicBabItems,
      sellingPlan,
      discountCodes,
      oneTimeAddon,
      addonGroupId,
    );
    await queryClient.fetchQuery(['subscription', customerId, subscriptionId]);
    closePanel();
  };

  const closePanel = () => {
    setSlidePanelAddon(undefined);
    setSelectedVariant(undefined);
    setSelectedBundle(undefined);
    setPanelOpen(false);
  };

  const handleSelect = ({
    id,
    quantity,
    lineSellingPolicy,
    addonGroupId,
  }: {
    id: string;
    quantity: number;
    lineSellingPolicy?: string;
    addonGroupId?: string;
  }) => {
    const addonLine = [
      createSCLLine({ id, quantity, lineSellingPolicy, addonGroupId }),
    ];
    saveAddonsAdded(addonLine);
    closePanel();
  };

  const handleConfirm = (selectedOption) => {
    const variant = addons?.find((v) => v.id == selectedOption.id);
    if (variant) {
      const format = getCurrencyFormatter(variant.currency ?? CURRENCY);
      const { price, discountedPrice } = variant;
      const formattedPrice = format(+price as number);
      const formattedDiscountedPrice = discountedPrice
        ? format(+discountedPrice)
        : undefined;
      setSelectedVariant({
        ...variant,
        price: formattedPrice,
        discountedPrice: formattedDiscountedPrice,
      });
    } else {
      setSelectedVariant(undefined);
    }
  };

  return {
    handleSelect,
    handleConfirm,
    handleSaveBundle,
    selectAddon,
    selectedBundle,
    slidePanelAddon,
    selectedVariant,
    isPanelOpen,
    closePanel,
  };
};

interface UseAddonsModalResponse {
  handleModalContent: (content, title: string) => void;
  openModal: () => void;
  closeModal: () => void;
}

export const useAddonsModal = (
  modalTitle: string,
  selectAddon: (addonId: number, isBundle: boolean) => void,
  addonProducts: AddonProduct[],
): UseAddonsModalResponse => {
  const { handleModalContent, openGlobalModal, closeGlobalModal } =
    useGlobalModalContext();

  const openModal = () => {
    openGlobalModal();
    handleModalContent(
      <AddonsModalContent addons={addonProducts} selectAddon={selectAddon} />,
      modalTitle,
    );
  };

  return {
    handleModalContent,
    openModal,
    closeModal: closeGlobalModal,
  };
};

const getOptionsAndValues = (productData) => {
  const options = productData.options;
  const options1 = new Set();
  const options2 = new Set();
  const options3 = new Set();
  productData.variants.forEach((variant) => {
    options1.add(variant.option1);
    options2.add(variant.option2);
    options3.add(variant.option3);
  });

  const optionsAndValues = options.map((optionName, i) => {
    const optionValue = [options1, options2, options3];
    const variantOption = [
      productData.variants[0].option1,
      productData.variants[0].option2,
      productData.variants[0].option3,
    ];
    const options = Array.from(optionValue[i]).map((name) => {
      return {
        label: name,
        id: name,
        type: 'option',
        optionName,
        isSelected: variantOption[i] === name,
      };
    });
    return [{ label: optionName, id: optionName, type: 'title' }, ...options];
  });

  return optionsAndValues;
};

export const transformVariantToMultiSelectOption = (
  variant: GlobalVariant,
  isSelected = false,
): MultiSelectOption => ({
  label: variant.title,
  id: `${variant.id}`,
  type: multiSelectType.OPTION,
  isSelected,
});

const createSCLLine = ({
  id,
  quantity,
  lineSellingPolicy,
  addonGroupId,
}: {
  id: string;
  quantity: number;
  lineSellingPolicy?: string;
  addonGroupId?: string;
}) => {
  const productVariantId = getGidFromIdForResource(id, 'ProductVariant');
  return {
    id: null,
    input: {
      quantity: quantity,
      productVariantId,
      isAddon: true,
      lineSellingPolicy,
      addonGroupId,
    },
  };
};

const getVariantDataFromAddonLines = (
  addonLines,
  addons: GlobalVariant[],
  newLines: LineResponse[],
  updatedPrices: ComputedPricingPolicy[],
  addonProducts: AddonProduct[],
): (GlobalVariant & {
  quantity: number;
  productName: string;
  pricingPolicy?: ComputedPricingPolicy;
} & {
  line: { id: string };
})[] => {
  const addonIds = addonLines.map((addon) => ({
    id: getIdFromGid(addon.input.productVariantId),
    quantity: addon.input.quantity,
  }));
  const variantData = addons
    .map((addon) => {
      const addonId = addonIds.find((a) => a.id === addon.id.toString());

      if (addonId) {
        const line = newLines.find(
          (l) => getIdFromGid(l.variantId) === addon.id.toString(),
        );
        const productName = addonProducts.find(
          (product) => product.productData.id === addon.productId,
        )?.productData.title;

        // We are having only one addon product so we can use the first element;
        const pricingPolicy = (updatedPrices && updatedPrices[0]) || undefined;

        return {
          ...addon,
          pricingPolicy,
          productName,
          quantity: addonId.quantity,
          line,
        };
      }
    })
    .filter(Boolean);

  return variantData as (GlobalVariant & {
    quantity: number;
    productName: string;
  } & {
    line: { id: string };
  })[];
};

const getVariantPriceRange = (variants: any[]) => {
  if (!variants?.length) return { min: null, max: null };

  const prices = variants.map((v) => Number(v.price));
  return {
    min: Math.min(...prices),
    max: Math.max(...prices),
  };
};

const transformResponse = (response, addonsDiscounts): AddonProduct[] => {
  const result = { ...emptyGlobalProduct };
  const data = response?.filter(
    ({ product: { productData } }) => productData.variants.length > 0,
  );
  return data?.map(
    ({ product: responseItem, discounts, sellingPolicy, addonGroupId }) => {
      const metafields = responseItem.productMetafields || {};
      const isBundle = !!metafields.bundle?.isBundle;

      const babType = metafields.bundle?.type;
      const product = responseItem.productData;
      const variantsInventory = responseItem.variantsInventory || [];
      const optionsAndValues = getOptionsAndValues(product);
      const variantsNames = product.variants.map((variant) => variant.title);
      const hasOnlyDefaultVariant =
        variantsNames.length === 1 &&
        variantsNames[0] === VARIANT_DEFAULT_TITLE;
      const variantsNameList = listFormatter(
        { type: 'conjunction', style: 'long' },
        variantsNames,
      );
      const discountedPrices = getDiscountedPrices(product, discounts) || {};
      const appliedDiscounts = discounts || addonsDiscounts;
      const currency =
        product?.contextualPricing?.minVariantPricing?.price?.currencyCode ||
        CURRENCY;

      const variants = product.variants.map((v) =>
        transformVariantToGlobal(
          v,
          product,
          hasOnlyDefaultVariant,
          currency,
          appliedDiscounts,
          variantsInventory[v.id],
        ),
      );

      const { min: variantPriceMin, max: variantPriceMax } =
        getVariantPriceRange(variants);

      const priceMin =
        variantPriceMin ??
        product?.contextualPricing?.minVariantPricing?.price?.amount;
      const priceMax =
        variantPriceMax ??
        product?.contextualPricing?.maxVariantPricing?.price?.amount;

      return {
        ...data,
        addonGroupId,
        productData: {
          ...result,
          id: product.id,
          title: product.title,
          name: variantsNameList,
          contextualPricing: product.contextualPricing,
          description: product.description,
          descriptionHtml: product.descriptionHtml,
          image: product.featured_image,
          price: priceMin,
          priceMin,
          priceMax,
          ...discountedPrices,
          available: product.available,
          options: product.options,
          optionsAndValues: flatten(optionsAndValues) as MultiSelectOption[],
          hasOnlyDefaultVariant,
          priceCurrency: currency,
          type: babType,
          productType: product.type,
          isBundle,
          tags: product.tags,
          addonSellingPolicy: sellingPolicy,
          variants,
          variantsOptions: product.variants.map((v) =>
            transformVariantToMultiSelectOption(v),
          ),
        },
      };
    },
  );
};

const transformVariantToGlobal = (
  variant,
  product,
  hasOnlyDefaultVariant,
  currency,
  addonsDiscounts = null,
  inventory = null,
): GlobalVariant => {
  const result = { ...emptyGlobalVariant };
  const image = product.featured_image;
  const productId = product.id;
  const productName = product.title;
  const title = hasOnlyDefaultVariant ? product.title : variant.title;
  const discountedPrices = getDiscountedPrices(variant, addonsDiscounts) || {};

  return {
    ...result,
    ...discountedPrices,
    addonsDiscounts,
    id: variant.id,
    name: variant.name,
    available: variant.available,
    productName,
    title,
    productId,
    contextualPricing: variant.contextualPricing,
    options: variant.options,
    price: +variant?.contextualPricing?.price?.amount,
    image: variant.featured_image ? variant.featured_image.src : image,
    description: product.description,
    descriptionHtml: product.descriptionHtml,
    sellingPlanAllocations: variant.selling_plan_allocations,
    currency,
    selectedOptions: [],
    inventory,
  };
};

const extractVariantAndImage = (
  { productData, hasOnlyDefaultVariant }: AddonProduct,
  addonsDiscounts?: AddonsDiscounts,
): GlobalVariant[] => {
  const result = { ...emptyGlobalVariant };
  const image = productData.image;
  const productId = productData.id;
  const productName = productData.title;
  const currency = productData.priceCurrency;

  return productData.variants.map((variant) => {
    const title = hasOnlyDefaultVariant ? productData.title : variant.title;
    const discountedPrices =
      getDiscountedPrices(variant, addonsDiscounts) || {};

    return {
      ...result,
      ...discountedPrices,
      id: variant.id,
      name: variant.name,
      available: variant.available,
      currency,
      productName,
      title,
      productId,
      options: variant.options,
      price: +variant.price,
      image: variant.image || image,
      description: productData.description,
      descriptionHtml: productData.descriptionHtml,
      sellingPlanAllocations: variant.sellingPlanAllocations,
    };
  });
};

const getDiscountedPrices = (
  product: any,
  addonsDiscounts: AddonsDiscounts | undefined,
) => {
  let discountedPriceMin =
    +product?.contextualPricing?.minVariantPricing?.price?.amount ||
    +product?.contextualPricing?.price?.amount;
  let discountedPrice = discountedPriceMin;
  let discountedPriceMax =
    +product?.contextualPricing?.maxVariantPricing?.price?.amount ||
    +product?.contextualPricing?.price?.amount;

  const type = addonsDiscounts?.type
    ? addonsDiscounts?.type
    : addonsDiscounts?.percentage && addonsDiscounts?.percentage !== 0
      ? 'PERCENTAGE'
      : addonsDiscounts?.amount && addonsDiscounts?.amount !== 0
        ? 'FIXED_AMOUNT'
        : null;

  if (type === 'FIXED_AMOUNT') {
    const amount = addonsDiscounts.amount || 0;
    const contextualizedDiscountAmount =
      amount * window.Shopify?.currency?.rate || 1;
    discountedPrice -= contextualizedDiscountAmount;
    discountedPriceMin -= contextualizedDiscountAmount;
    discountedPriceMax -= contextualizedDiscountAmount;

    return {
      discountedPrice,
      discountedPriceMin,
      discountedPriceMax,
    };
  }

  if (type === 'PERCENTAGE') {
    const percentage = (addonsDiscounts.percentage || 0) / 100;
    discountedPrice -= discountedPrice * percentage;
    discountedPriceMin -= discountedPriceMin * percentage;
    discountedPriceMax -= discountedPriceMax * percentage;

    return {
      discountedPrice,
      discountedPriceMin,
      discountedPriceMax,
    };
  }

  return null;
};

export const addonIsBaB = (addon: GlobalProduct): boolean =>
  (addon.isBundle && addon.type === BundleType.classicBaB) ||
  addon.type === BundleType.dynamicBaB;
